import React, { memo, useMemo } from 'react';
import classNames from 'classnames';
import { formGroup, formControl, FormDataType } from '@core/utils/form';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { useConfigureBlock } from '@modules/course/CourseBuilder/hooks';
import { Container } from 'reactstrap';
import { css } from '@emotion/react';
import { HTMLService } from '@business/services';
import { R } from '@core/utils/r';
import { useCourseBuilderContext } from '@modules/course/CourseBuilder/CourseBuilderPage';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { CourseBuilder } from '../types';
import { BlockContainer } from '../BlockContainer';
import { ProcedureBlockSettings } from './ProcedureBlockSettings';
import { GlossaryService } from '@business/services/glossary_service';

function defineProcedureBlockForm(variation: ProcedureBlockVariation) {
  return formGroup({
    heading: formControl<string>({ initialValue: '' }),
    text: formControl<string>({
      initialValue: `<ol><li>Vel magna iaculis arcu cursus parturient blandit suscipit taciti auctor maecenas.</li><li>Penatibus libero risus montes conubia pretium quisque ut fermentum, vulputate augue litora ornare fames turpis convallis imperdiet, curae etiam sed enim dictumst purus sit.</li><li>Tortor parturient gravida habitant dapibus et fusce felis nam nibh litora ridiculus aenean porttitor turpis pharetra.</li><li>Suscipit montes natoque aptent ex gravida orci ultrices volutpat nunc.</li></ol>`,
    }),
    id: formControl<string>({
      label: R.startCase(variation),
    }),
    advance: defineBlockAdvanceForm({ paddingTop: 0, paddingBottom: 0 }),
  });
}

export type ProcedureWidgetFormData = FormDataType<typeof defineProcedureBlockForm>;
export type ProcedureWidgetForm = ReturnType<typeof defineProcedureBlockForm>;

export enum ProcedureBlockVariation {
  procedure = 'procedure',
}

export const ProcedureBlock = memo((props: CourseBuilder.BlockProps<ProcedureBlockVariation>) => {
  const { variation } = props;
  const form = useMemo(() => defineProcedureBlockForm(variation), [variation]);
  const formValue = useNonNilObservable(form.value$);
  const proposal = useCourseBuilderContext();
  const primaryColor = useMemo(() => proposal?.form.value.primaryColor, [proposal]);

  const listCss = useMemo(() => {
    if (primaryColor) {
      return css`
        li:before {
          background-color: ${primaryColor};
        }
      `;
    } else {
      return css`
        li:before {
          background-color: #45a8f7;
        }
      `;
    }
  }, [primaryColor]);

  useConfigureBlock({
    props,
    form,
    contentComponent: ProcedureBlockSettings,
    contentProps: { form, variation },
  });

  return (
    <Container>
      <BlockContainer className={classNames('d-flex flex-fill', 'block-list', variation)} advanceForm={formValue.advance}>
        {!R.isEmpty(formValue.heading) && <h3>{formValue.heading}</h3>}
        <div css={listCss}>{GlossaryService.getGlossaryContent(formValue.text)}</div>
      </BlockContainer>
    </Container>
  );
});
